import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DataScopeStatus, IDataScope } from '../../modules/auth/models/IDataScope';
import { flatten } from '../../modules/shared/utils/flatten';
import { LocationPickerLayout } from '../../modules/customer-shared/models/LocationPickerLayout';
import { AuthState } from './auth.reducer';
import { selectMergedRouteParams } from "../router/selectors";

export const selectAuthState = createFeatureSelector<AuthState>('auth');
export const isAuthenticated = createSelector(selectAuthState, state => !!(state.user && state.token));
export const selectUser = createSelector(selectAuthState, state => {
  if (state.impersonatedAs?.user) return state.impersonatedAs.user;
  return state.user;
});
export const selectUserLoading = createSelector(selectAuthState, state => state.userLoading);

export const selectAvailableCustomers = createSelector(selectUser, user =>
  user ? user.customers.filter(c => c.status === DataScopeStatus.Active) : [],
);

export const selectAvailableLocations = createSelector(selectUser, user => {
  if (!user || !user.customers) return [];
  let locationList = flatten(
    user.customers
      .filter(c => c.status === DataScopeStatus.Active)
      .map(cus =>
        cus.children.map(loc => ({
          ...loc,
          customer: cus.name,
          customerId: cus.id,
        })),
      ),
  ).sort((a, b) => (a.name > b.name ? 1 : -1));

  let locNames = locationList.map(x => x.name.trim().toLowerCase());

  return locationList.map(x => ({
    unique: locNames.filter(n => n === x.name.trim().toLowerCase()).length === 1,
    ...x,
  }));
});

export const selectCurrencySymbol = createSelector(selectMergedRouteParams, selectAvailableLocations, (params, locations) => {
  if (+params.areaId) {
    const location = locations.find(loc => loc.children.find(a => a.id === +params.areaId));
    return location ? location.currencySymbol : '$';
  }
  else if (+params.locationId) {
    const location = locations.find(loc => loc.id === +params.locationId);
    return location ? location.currencySymbol : '$';
  }
  return locations.length > 0 ? locations[0].currencySymbol : '$';
});

export const selectCurrencyCode = createSelector(selectMergedRouteParams, selectAvailableLocations, (params, locations) => {
  if (+params.areaId) {
    const location = locations.find(loc => loc.children.find(a => a.id === +params.areaId));
    return location ? location.currencyCode : 'USD';
  }
  else if (+params.locationId) {
    const location = locations.find(loc => loc.id === +params.locationId);
    return location ? location.currencyCode : 'USD';
  }
  return locations.length > 0 ? locations[0].currencyCode : 'USD';
});

export const selectLocationsOfCustomer = (customerId: number) =>
  createSelector(selectUser, user => (customerId ? user.customers.find(c => c.id === customerId).children : ([] as IDataScope[])));

export const selectAreasOfLocation = (customerId: number, locationId: number) =>
  createSelector(selectLocationsOfCustomer(customerId), locations =>
    locationId ? locations.find(l => l.id === locationId).children : ([] as IDataScope[]),
  );

export const selectAreasByLocation = (locationId: number) =>
  createSelector(selectUser, user => {
    if (!user || !locationId) return [] as IDataScope[];
    for (const customer of user.customers) {
      const location = customer.children.find(l => l.id === locationId);
      if (location) return location.children;
    }
    return [] as IDataScope[];
  });

export const selectAllLocations = createSelector(selectUser, user =>
  user
    ? (user.customers.reduce(
        (locs: { [locId: number]: IDataScope }, customer: IDataScope) =>
          Object.assign(locs, ...customer.children.map(loc => ({ [loc.id]: loc }))),
        {} as { [locId: number]: IDataScope },
      ) as { [locId: number]: IDataScope })
    : {},
);

export const selectHierarchyByAreaId = (areaId: number) =>
  createSelector(selectUser, user => {
    for (const c of user.customers) {
      for (const l of c.children) {
        for (const a of l.children) {
          if (a.id === areaId)
            return {
              customer: c,
              location: l,
              area: a,
            };
        }
      }
    }
    return null;
  });

export const selectIsAdmin = createSelector(selectUser, user => user !== null && user.roles && user.roles.includes('Administrator'));
export const selectIsImpersonated = createSelector(selectAuthState, state => !!state.impersonatedAs);

export const selectRoleHomePage = createSelector(selectUser, user => {
  if (!user) return '/login';
  if (user.roles.includes('Auditor')) return '/auditing';
  if (user.roles.includes('Administrator')) return '/admin';
  if (user.roles.includes('Customer')) return '/audits';
  return '/';
});

export const selectAuthInfo = createSelector(selectAuthState, isAuthenticated, (state, isAuthenticated) => ({
  user: state.impersonatedAs?.user ?? state.user,
  token: state.impersonatedAs?.token ?? state.token,
  isAuthenticated,
}));

export const selectIsExecutive = createSelector(
  selectUser,
  state => state?.customers?.some(x => x.isExecutive) && state?.roles?.every(r => r === 'Customer'),
);

export const selectUserDataCategory = createSelector(selectUser, user => {
  if (!user) {
    return LocationPickerLayout.Unknown; // this is a hack to prevent error messages @ logout
  }
  if (!user.customers.length || !user.customers[0].children.length || !user.customers[0].children[0].children.length) {
    return LocationPickerLayout.Unknown;
  } else if (user.customers.length > 1) {
    return LocationPickerLayout.MultiCustomer;
  } else if (user.customers[0].children.length > 1) {
    return LocationPickerLayout.MultiLocation;
  } else if (user.customers[0].children[0].children.length > 1) {
    return LocationPickerLayout.MultiArea;
  } else {
    return LocationPickerLayout.SingleArea;
  }
});

export const selectAllAreas = createSelector(selectUser, user => {
  const areas: {
    id: number;
    area: string;
    customerId: number;
    customer: string;
    locationId: number;
    location: string;
  }[] = [];
  if (!user) return areas;

  for (const c of user.customers) {
    for (const l of c.children) {
      for (const a of l.children) {
        areas.push({
          id: a.id,
          area: a.name,
          customer: c.name,
          customerId: c.id,
          location: l.name,
          locationId: l.id,
        });
      }
    }
  }
  return areas;
});

export const selectSectionsOfAreas = (customerId: number, locationId: number, areaId: number) =>
  createSelector(selectAreasOfLocation(customerId, locationId), areas =>
    areaId ? areas.find(l => l.id === areaId).children : ([] as IDataScope[]),
  );

export const selectCamerasOfSections = (customerId: number, locationId: number, areaId: number, sectionId: number) =>
  createSelector(selectSectionsOfAreas(customerId, locationId, areaId), sections =>
    sectionId ? sections.find(l => l.id === sectionId).children : ([] as IDataScope[]),
  );

export const selectFeatures = createSelector(selectAuthState, state => state.features.list);
export const selectRecentFeaturesCount = createSelector(selectAuthState, state => state.features.recentCount);
